import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — BigBrandBooster Digital Marketing Agency" },
      { name: "description", content: "Have a project in mind? Get in touch with BigBrandBooster — our team replies within one business day." },
      { property: "og:title", content: "Contact — BigBrandBooster" },
      { property: "og:description", content: "Get in touch with the BigBrandBooster team." },
    ],
    links: [{ rel: "canonical", href: "/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [sent, setSent] = useState(false);
  return (
    <>
      <PageHero
        eyebrow="Contact Us"
         gradientClass="bg-portfolio-gradient"
        title={<>Start Your <span className="text-accent">Growth Journey</span> Together</>}
        description="Have a project in mind or need expert advice? Our team is here to help you achieve your digital marketing goals."
      >
        <a href="tel:+918810201567" className="inline-flex items-center gap-3 rounded-2xl border border-primary-foreground/30 bg-primary-foreground/10 backdrop-blur px-5 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20">
          <Phone className="h-4 w-4" /> +91 8810201567
        </a>
        <a href="mailto:info@BigBrandBooster.com" className="inline-flex items-center gap-3 rounded-2xl border border-primary-foreground/30 bg-primary-foreground/10 backdrop-blur px-5 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20">
          <Mail className="h-4 w-4" /> info@BigBrandBooster.com
        </a>
      </PageHero>

      <section className="container-page py-24 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ink">Send Us a Message</h2>
          <p className="mt-3 text-muted-foreground">Tell us about your goals — we'll reply within one business day.</p>

          <form
            onSubmit={(e) => { e.preventDefault(); setSent(true); }}
            className="mt-10 grid sm:grid-cols-2 gap-5"
          >
            <Field label="Full name" name="name" placeholder="Sanjeev Kumar" />
            <Field label="Work email" name="email" type="email" placeholder="sanju@company.com" />
            <Field label="Company" name="company" placeholder="test pvt ltd" />
            <Field label="Phone" name="phone" placeholder="+91 99XXX XXXXX" />
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-ink mb-2">How can we help?</label>
              <textarea
                rows={5}
                placeholder="A few lines about your goals, timeline and budget."
                className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition"
              />
            </div>
            <div className="sm:col-span-2 flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">By submitting, you agree to our privacy policy.</p>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-full bg-primary-gradient px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-elegant hover:opacity-95"
              >
                {sent ? "Message sent ✓" : (<>Send message <Send className="h-4 w-4" /></>)}
              </button>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-2">
          <div className="rounded-3xl bg-ink text-background p-8 shadow-elegant">
            <h3 className="font-display text-2xl font-bold">Get in Touch</h3>
            <p className="mt-2 text-background/70 text-sm">Reach our team directly — we're online Mon–Fri, 9am–7pm IST.</p>
            <ul className="mt-8 space-y-6">
              <Info icon={<Phone className="h-4 w-4" />} label="Call us" value="+91 9818107944" />
              <Info icon={<Mail className="h-4 w-4" />} label="Email" value="info@BigBrandBooster.com" />
              <Info icon={<MapPin className="h-4 w-4" />} label="Headquarters" value="Sec 82 Gurgaon" />
            </ul>
            <div className="mt-10 rounded-2xl bg-background/5 p-5 border border-background/10">
              <p className="text-sm text-background/80">"Thanks BigBrand Booster for working as a growth partner for us"</p>
              <p className="mt-3 text-xs font-semibold">— Puneet Jain, CEO at Workday</p>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}

function Field({ label, name, type = "text", placeholder }: { label: string; name: string; type?: string; placeholder?: string }) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold text-ink mb-2">{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition"
      />
    </div>
  );
}

function Info({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <li className="flex items-start gap-4">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-gradient text-primary-foreground">{icon}</span>
      <div>
        <div className="text-xs uppercase tracking-widest text-background/60">{label}</div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </li>
  );
}
